import React from 'react';

/**
 * Renders chat text with URLs, emails and phone numbers turned into links,
 * the way WhatsApp does. Built as React nodes (never dangerouslySetInnerHTML)
 * so message content can't inject markup.
 */

// One alternation, matched in priority order: scheme'd URL / bare www. host /
// email / phone. Phones need a leading + or 10+ digits so amounts, PNRs and
// dates don't turn blue.
const TOKEN_RE =
  /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|[\w.+-]+@[\w-]+\.[\w.-]+|\+\d[\d\s-]{7,}\d|\b\d{10,13}\b)/gi;

/** Trailing punctuation belongs to the sentence, not the link. */
const trimTrailing = (token: string): { core: string; tail: string } => {
  let core = token;
  let tail = '';
  for (;;) {
    const last = core.at(-1);
    if (!last) break;
    const isPunct = '.,;:!?'.includes(last);
    // Only strip a closing bracket the token doesn't open itself, so
    // https://en.wikipedia.org/wiki/Foo_(bar) survives intact.
    const isUnbalanced =
      (last === ')' && !core.includes('(')) ||
      (last === ']' && !core.includes('[')) ||
      (last === '}' && !core.includes('{'));
    if (!isPunct && !isUnbalanced) break;
    tail = last + tail;
    core = core.slice(0, -1);
  }
  return { core, tail };
};

const hrefFor = (token: string): string | null => {
  if (/^https?:\/\//i.test(token)) return token;
  if (/^www\./i.test(token)) return `https://${token}`;
  if (token.includes('@')) return `mailto:${token}`;
  const digits = token.replace(/[^\d]/g, '');
  if (digits.length >= 10 && digits.length <= 15) return `tel:${token.replace(/\s/g, '')}`;
  return null;
};

// WhatsApp's own inline markup: *bold*, _italic_, ~strike~. The marker must
// hug the text and sit at a word edge, so snake_case names and "5 * 3" stay
// as typed.
const FORMAT_RE = /(?<![\w*_~])([*_~])(?=\S)([^\n]*?\S)\1(?![\w*_~])/g;

const formatInline = (text: string, keyPrefix: string): React.ReactNode[] => {
  const out: React.ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  for (const match of text.matchAll(FORMAT_RE)) {
    const [raw, marker, inner] = match;
    if (match.index > cursor) out.push(text.slice(cursor, match.index));
    const k = `${keyPrefix}f${key++}`;
    out.push(
      marker === '*' ? (
        <strong key={k}>{inner}</strong>
      ) : marker === '_' ? (
        <em key={k}>{inner}</em>
      ) : (
        <s key={k}>{inner}</s>
      ),
    );
    cursor = match.index + raw.length;
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
};

export const LinkifiedText = ({ text }: { text: string }): React.JSX.Element => {
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  let key = 0;

  for (const match of text.matchAll(TOKEN_RE)) {
    const raw = match[0];
    const start = match.index;
    const { core, tail } = trimTrailing(raw);
    const href = hrefFor(core);
    if (!href) continue;

    if (start > cursor) nodes.push(...formatInline(text.slice(cursor, start), `t${key}`));
    nodes.push(
      <a
        key={`l${key++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        // Underlined + inherited colour reads as a link on both the white
        // inbound bubble and the green outbound one.
        className="text-sky-700 underline decoration-sky-700/40 underline-offset-2 hover:decoration-sky-700"
        onClick={(e) => e.stopPropagation()}
      >
        {core}
      </a>,
    );
    if (tail) nodes.push(tail);
    cursor = start + raw.length;
  }

  if (cursor < text.length) nodes.push(...formatInline(text.slice(cursor), 'tail'));
  return <>{nodes}</>;
};
